import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.jsx';
import { AuthService } from '../services/auth-service.js';
import { useCreditConfig } from '../services/credit-config.js';
import { useCredits } from '../services/credits-service.js';
import { RevenueCatService } from '../services/revenuecat-service.js';
import { BillingService } from '../services/billing-service.js';
import { isNativeApp } from '../services/platform-service.js';
import { logEvent, analytics } from '../firebase.js';

function formatPrice({ amount, currency }) {
  const cur = String(currency || 'usd').toUpperCase();
  try {
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency: cur }).resolvedOptions().maximumFractionDigits;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(amount / 10 ** digits);
  } catch {
    return `${(amount / 100).toFixed(2)} ${cur}`;
  }
}

export function CreditModal({ open, onClose, onSignInRequest }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const creditCfg = useCreditConfig();
  // Guest 는 초대 메뉴 대신 로그인 CTA 가 우선. 익명 상태에서 Invite 를
  // 보여줘봐야 결국 로그인 게이트로 막힘 — confusion 만 늘림.
  const current = AuthService.currentUser;
  const isGuest = !current || current.isAnonymous;

  // 크레딧 팩 — 앱은 스토어 결제(RevenueCat), 웹은 Stripe Checkout (Apple Pay /
  // Google Pay / 카드). 앱 안에서 웹 결제를 보여주면 Apple 3.1.1 위반이라 갈라 둔다.
  // 지급은 서버 웹훅이 하고 잔액은 스냅샷으로 들어온다 — 늘어나는 걸 보고 "충전됐어요".
  const credits = useCredits();
  const native = isNativeApp();
  const [packs, setPacks] = useState(null);
  const [buying, setBuying] = useState(null);   // productId
  const [packNote, setPackNote] = useState(null); // 'waiting' | 'done' | 'slow' | 'failed'
  const before = useRef(null);
  const canBuy = open && !isGuest;
  useEffect(() => {
    if (!canBuy) return;
    let stale = false;
    setPackNote(null);
    const load = native
      ? RevenueCatService.getCreditPacks(creditCfg.packs)
      : BillingService.listWebPacks().then((list) => list.map((p) => ({ ...p, priceString: formatPrice(p) })));
    load.then((list) => {
      if (stale) return;
      setPacks(list);
      if (list.length) logEvent(analytics, 'pack_sheet_shown', { balance: credits.credits ?? -1 });
    });
    return () => { stale = true; };
  }, [canBuy]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (packNote !== 'waiting' || before.current === null) return;
    if ((credits.credits ?? 0) > before.current) {
      setPackNote('done');
      const tm = setTimeout(() => onClose?.(), 1400);
      return () => clearTimeout(tm);
    }
    const tm = setTimeout(() => setPackNote((n) => (n === 'waiting' ? 'slow' : n)), 30 * 1000);
    return () => clearTimeout(tm);
  }, [credits.credits, packNote]); // eslint-disable-line react-hooks/exhaustive-deps
  const buy = async (p) => {
    if (buying) return;
    let leaving = false;
    setBuying(p.productId);
    setPackNote(null);
    logEvent(analytics, 'pack_purchase_started', { product: p.productId });
    try {
      before.current = credits.credits ?? 0;
      if (!native) {
        await BillingService.startPackCheckout(p.productId);
        leaving = true;
        return; // Stripe 로 넘어간다 — 결제 확인(pack_purchased)은 웹훅·잔액으로.
      }
      const r = await RevenueCatService.purchasePack(p.pkg, AuthService.currentUser?.uid);
      if (r === 'cancelled') {
        logEvent(analytics, 'pack_purchase_cancelled', { product: p.productId });
        return;
      }
      logEvent(analytics, 'pack_purchased', { product: p.productId, credits: p.credits });
      setPackNote('waiting');
    } catch (e) {
      console.warn('pack purchase failed', e?.message);
      logEvent(analytics, 'pack_purchase_failed', { product: p.productId });
      setPackNote('failed');
    } finally {
      if (!leaving) setBuying(null); // 결제창으로 넘어가는 동안은 "여는 중…" 유지
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // 벽(생성 1장 값도 안 남음)에서 열렸으면 "내일 다시 채워져요" 와 "지금 충전" 을 한 문장에
  // 나란히 — 예전 문구는 기다리라는 쪽으로만 밀었다. 잔액 칩으로 연 경우는 그대로.
  const freePerDay = Math.floor(creditCfg.daily / creditCfg.price.generate);
  const chatFree = creditCfg.price.chat === 0 && creditCfg.price.chatPhoto === 0;
  const atWall = !isGuest && credits.plan !== 'pro' && credits.credits !== null
    && credits.credits < creditCfg.price.generate;

  const goto = (path) => {
    onClose?.();
    navigate(path);
  };

  const signIn = () => {
    onClose?.();
    onSignInRequest?.();
  };

  // 구독 판매는 접었다. 크레딧을 늘릴 방법은 (a) 내일까지 기다리기 (b) 친구 초대
  // (c) 앱에서 크레딧 팩 (2.3.0) — 팩은 아래 별도 섹션.
  const options = isGuest
    ? [
        {
          icon: 'login',
          title: t('creditOptionSignIn'),
          desc: t('creditOptionSignInDesc'),
          onClick: signIn,
          highlight: true,
        },
      ]
    : [
        {
          icon: 'group_add',
          title: t('creditOptionInvite'),
          desc: t('creditOptionInviteDesc', { n: creditCfg.inviteReward }),
          onClick: () => goto('/invite'),
          highlight: true,
        },
      ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card credit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3 className="credit-modal-title">
          {isGuest ? t('creditModalGuestTitle') : t('creditModalTitle')}
        </h3>
        <p className="credit-modal-desc">
          {isGuest ? t('creditModalGuestDesc')
            : atWall ? t('creditModalWallDesc', { n: freePerDay })
            : t('creditModalDesc')}
        </p>

        {canBuy && packs && packs.length > 0 && (
          <div className="credit-packs">
            <p className="credit-packs-title">{t('packsTitle')}</p>
            <div className="credit-packs-row">
              {packs.map((p) => (
                <button
                  key={p.productId}
                  type="button"
                  className="credit-pack"
                  disabled={!!buying || packNote === 'waiting'}
                  onClick={() => buy(p)}
                >
                  <span className="credit-pack-credits">
                    <i className="material-symbols-rounded">bolt</i>{p.credits}
                  </span>
                  <span className="credit-pack-sub">{t('packDesigns', { n: Math.floor(p.credits / creditCfg.price.generate) })}</span>
                  <span className="credit-pack-price">{buying === p.productId ? t('packBuying') : p.priceString}</span>
                </button>
              ))}
            </div>
            {packNote && (
              <p className={`credit-pack-note${packNote === 'failed' ? ' is-error' : ''}`} aria-live="polite">
                {t({ waiting: 'packWaiting', done: 'packDone', slow: 'packSlow', failed: 'packFailed' }[packNote])}
              </p>
            )}
          </div>
        )}

        <ul className="credit-options">
          {options.map((opt) => {
            const Tag = opt.onClick ? 'button' : 'div';
            return (
              <li key={opt.title} className="credit-option-wrap">
                <Tag
                  type={opt.onClick ? 'button' : undefined}
                  className={`credit-option${opt.onClick ? ' credit-option-clickable' : ''}${opt.highlight ? ' credit-option-highlight' : ''}`}
                  onClick={opt.onClick}
                  disabled={!opt.onClick && undefined}
                >
                  <i className="material-symbols-rounded credit-option-icon">{opt.icon}</i>
                  <div className="credit-option-body">
                    <div className="credit-option-title">
                      <span className="credit-option-title-text">{opt.title}</span>
                      {opt.comingSoon && (
                        <span className="credit-option-tag">{t('comingSoon')}</span>
                      )}
                    </div>
                    <div className="credit-option-desc">{opt.desc}</div>
                  </div>
                  {opt.onClick && (
                    <i className="material-symbols-rounded credit-option-chevron">chevron_right</i>
                  )}
                </Tag>
              </li>
            );
          })}
        </ul>

        {!isGuest && !atWall && (
          <p className="credit-daily-hint">
            <i className="material-symbols-rounded">auto_awesome</i>
            {t('dailyBonusHint', { n: creditCfg.daily })}
          </p>
        )}
        {/* 벽에서도 앱에 남을 이유 — 만들기는 멈춰도 Gio 와 대화는 계속된다. */}
        {atWall && chatFree && (
          <p className="credit-daily-hint">
            <i className="material-symbols-rounded">forum</i>
            {t('creditModalChatFree')}
          </p>
        )}
      </div>
    </div>
  );
}
